import { ActionIcon, Flex, Text } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import cn from 'classnames';
import type { EmblaOptionsType } from 'embla-carousel';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { matchPath, useLocation, useParams } from 'react-router-dom';

import src from '@/assets/images/removal.png';
import Loader from '@/components/ui/Loader';
import { ROUTES } from '@/configs/routes';
import { useEmblaAutoHeightCarousel } from '@/hooks/useEmblaAutoHeightCarousel';
import { organizationsStore } from '@/stores/global/RootStore';
import { useOrganizationChatAiPageStore } from '@/stores/local/pages/OrganizationChatAiPageStore';
import { useOrganizationSectionsStore } from '@/stores/local/shared/OrganizationSectionsStore';
import { useDevices } from '@/utils/devices';

import Slide from './Slide';
import type { CarouselSlide } from './types';

import s from './EmptyState.module.scss';

type EmptyStateProps = {
  className?: string;
  isDrawer?: boolean;
  /** Если задан — чат в drawer привязан к одному документу (не «вся организация») */
  drawerFixedDocumentId?: string;
};

const EmptyState: React.FC<EmptyStateProps> = ({ className, isDrawer = false, drawerFixedDocumentId }) => {
  const { isCompactViewport, isDesktop } = useDevices();
  const { id: organizationIdParam } = useParams();
  const organizationId = organizationIdParam ?? '';

  const { pathname } = useLocation();
  const { eventsOrganizationListStore, latestDocumentsStore } = useOrganizationChatAiPageStore();
  const sectionsStore = useOrganizationSectionsStore();
  const isAgendaEnabled = sectionsStore.isSectionEnabled('agenda');

  const isDocumentsChatPage = React.useMemo(
    () => Boolean(matchPath(ROUTES.organization.documents.chat.path, pathname)),
    [pathname],
  );

  const isLatestDocumentsEnabled = sectionsStore.isSectionEnabled('latest-documents');

  // Логика: !agenda && latest → карусель + последние 10 док.; !agenda && !latest → только чат; agenda && latest → карусель + event.documents
  const showCarousel = isAgendaEnabled || isLatestDocumentsEnabled;
  const passLatestDocuments = !isAgendaEnabled && isLatestDocumentsEnabled;

  React.useEffect(() => {
    if (isDrawer || isDocumentsChatPage || !showCarousel || !organizationId) {
      return;
    }

    organizationsStore.fetchOrganizations();
  }, [isDrawer, isDocumentsChatPage, showCarousel, organizationId]);

  React.useEffect(() => {
    if (isDrawer || isDocumentsChatPage || !showCarousel) {
      return;
    }

    eventsOrganizationListStore.fetchEvents();
    if (isLatestDocumentsEnabled) {
      latestDocumentsStore.fetchLatestDocuments();
    }
  }, [
    isDrawer,
    isDocumentsChatPage,
    showCarousel,
    isLatestDocumentsEnabled,
    latestDocumentsStore,
    eventsOrganizationListStore,
  ]);

  const { events, eventsTotal } = eventsOrganizationListStore;
  const latestDocuments = latestDocumentsStore.items;

  const slides = React.useMemo<CarouselSlide[]>(() => {
    if (events.length > 0) {
      return events.map((event) => ({ type: 'event', event }));
    }

    return [];
  }, [events]);
  const slideCount = slides.length;
  const carouselKey = React.useMemo(() => slides.map((s) => String(s.event.id)).join('-'), [slides]);
  const initialSlideIndex = React.useRef(eventsOrganizationListStore.activeEventIndex).current;
  const watchDrag = React.useCallback<Exclude<EmblaOptionsType['watchDrag'], boolean | undefined>>(
    (_emblaApi, event) => {
      const target = event.target;
      const element = target instanceof Element ? target : target instanceof Node ? target.parentElement : null;

      if (!element) {
        return true;
      }

      return !element.closest('[data-embla-drag-ignore]');
    },
    [],
  );
  const { emblaApi, emblaRef, selectedIndex, canScrollPrev, canScrollNext, hasMultipleSlides } =
    useEmblaAutoHeightCarousel({
      slideCount,
      initialIndex: initialSlideIndex ?? 0,
      reInitKey: carouselKey,
      onSelect: eventsOrganizationListStore.setActiveEventIndex,
      watchDrag,
      useAutoHeight: !isCompactViewport,
    });

  const handlePrev = React.useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const handleNext = React.useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const slidesTotal = events.length > 0 ? eventsTotal || events.length : slideCount;
  const currentSlideIndex = Math.min(selectedIndex + 1, slidesTotal);

  const renderStartChatPlaceholder = (
    <Flex
      className={cn(s.root_empty, className)}
      align="center"
      justify="center"
      direction="column"
      data-scroll-lock-allow
    >
      {!isCompactViewport && <img src={src} width={140} height={140} />}
      <Text className={s.root__title} size={isCompactViewport ? 'md' : 'lg'} fw={600}>
        Начните общение с умным помощником
      </Text>
      <Text className={s.root__caption} size={isCompactViewport ? 'sm' : 'md'}>
        {isDrawer && drawerFixedDocumentId
          ? 'ИИ отвечает по текущему документу, а если в нём нет нужных сведений — ищет в других материалах. Задайте вопрос.'
          : isDocumentsChatPage && isDesktop
            ? 'ИИ сможет отвечать на вопросы по любым документам организации. Выберите один или несколько документов из списка и задайте вопрос.'
            : 'ИИ сможет отвечать на вопросы по любым документам организации. Задайте вопрос.'}
      </Text>
    </Flex>
  );

  if (isDrawer || isDocumentsChatPage || !showCarousel) {
    return renderStartChatPlaceholder;
  }

  if (eventsOrganizationListStore.meta.isLoading) {
    return <Loader type="dots" fullHeight text="Ищу события..." />;
  }

  if (slideCount === 0) {
    return renderStartChatPlaceholder;
  }

  return (
    <Flex className={s.root} align="center" justify="center" direction="column" data-scroll-lock-allow>
      <div className={s.root__content}>
        <div key={carouselKey} className={s.root__carousel}>
          <Flex
            align="center"
            justify={hasMultipleSlides ? 'space-between' : 'flex-end'}
            className={s.root__controls}
            gap="xs"
          >
            {hasMultipleSlides && (
              <ActionIcon
                variant={isCompactViewport ? 'light' : 'filled'}
                onClick={handlePrev}
                disabled={!canScrollPrev}
              >
                <IconChevronLeft size={18} />
              </ActionIcon>
            )}

            {hasMultipleSlides && (
              <Text size="sm" c="gray" className={s.root__counter}>
                {currentSlideIndex} / {slidesTotal}
              </Text>
            )}

            {hasMultipleSlides && (
              <ActionIcon
                variant={isCompactViewport ? 'light' : 'filled'}
                onClick={handleNext}
                disabled={!canScrollNext}
              >
                <IconChevronRight size={18} />
              </ActionIcon>
            )}
          </Flex>

          <div className={s.root__viewport} ref={emblaRef}>
            <div className={s.root__container}>
              {slides.map((slide) => (
                <div className={s.root__slide} key={slide.event.id}>
                  <Slide
                    event={slide.event}
                    dateInfo={eventsOrganizationListStore.getEventDateInfo(slide.event)}
                    latestDocuments={passLatestDocuments ? latestDocuments : undefined}
                    forceLatestDocuments={passLatestDocuments}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Flex>
  );
};

export default observer(EmptyState);
